import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import StatesDropDown from "../../components/StatesDropDownMenu";
import PhoneInput from "../../components/PhoneInput";
import SourceOfInfoDropDown from "../../components/SourceOfInfoDropDown";
import { REGEXES } from "../../lib/constants";
import { useSendContactUsEmailMutation } from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import Loader from "../../components/LoaderComponent";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  companyName?: string;
  companyWebsite?: string;
  businessAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  numberOfStores?: string;
  heardAboutUs?: string;
  message: string;
}

const ContactUs: React.FC = () => {
  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    reset,
  } = useForm<FormData>();
  const [sendContactUsApi, { isLoading }] = useSendContactUsEmailMutation();

  const { toast } = useToast();

  const defaultFormValues: FormData = {
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    phoneNumber: undefined,
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    companyWebsite: undefined,
    numberOfStores: "",
    message: "",
    heardAboutUs: "",
  };

  const [formData, setFormData] = useState<FormData>(defaultFormValues);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCode, setSelectedCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [infoSource, setInfoSource] = useState("");

  const handleStateChange = (selectedState: string) => {
    setValue("state", selectedState, { shouldValidate: true });
    setSelectedState(selectedState);
    clearErrors("state");
  };
  const handleCodeChange = (newCode: string) => {
    setSelectedCode(newCode);
  };

  const handlePhoneInputChange = (phone: string) => {
    setValue("phoneNumber", phone);
    setPhoneNumber(phone);
    clearErrors("phoneNumber");
  };

  const handleSourceOfInfoChange = (sourceOfInfo: string) => {
    setValue("heardAboutUs", sourceOfInfo);
    setInfoSource(sourceOfInfo);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    clearErrors(name as keyof FormData);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      const formattedPhone =
        data.phoneNumber !== undefined && data.phoneNumber!.trim() !== ""
          ? selectedCode + data.phoneNumber
          : undefined;
      const stores = parseInt(data.numberOfStores as string);
      const finalData = {
        ...data,
        phoneNumber: formattedPhone,
        numberOfStores: stores,
      };

      const response = await sendContactUsApi({
        contactFormSubmissionDto: finalData,
      }).unwrap();
      toast({
        title: "Success",
        description: `${response?.message}`,
      });

      reset(defaultFormValues);
      setFormData(defaultFormValues);
      setSelectedState("");
      setPhoneNumber("");
      setSelectedCode("+1");
      setInfoSource("");
    } catch (error: any) {
      if (error === undefined) {
        toast({
          title: "Error",
          description: "Something went wrong",
        });
      } else {
        toast({
          title: "Error",
          description: `${error.data.message}`,
        });
      }
    }
  };

  return (
    <div className="flex justify-center items-center p-4  md:px-[15vw] md:py-5 min-h-max">
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="new-password"
        className="px-6 bg-white w-full grid md:grid-cols-2 grid-cols-1 gap-x-4"
      >
        <h2 className="md:col-span-2 col-span-1 text-2xl text-left font-bold mb-6">
          Your Information
        </h2>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="firstName"
            className="block text-sm font-semibold mb-1"
          >
            First Name<span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            autoComplete="new-password"
            value={formData.firstName}
            {...register("firstName", { required: "This field is required" })}
            onChange={handleChange}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.firstName
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors.firstName && (
            <p className="text-red-500">{errors.firstName.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="lastName"
            className="block text-sm font-semibold mb-1"
          >
            Last Name<span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            autoComplete="new-password"
            value={formData.lastName}
            {...register("lastName", { required: "This field is required" })}
            onChange={handleChange}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.lastName
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors.lastName && (
            <p className="text-red-500">{errors.lastName.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label htmlFor="email" className="block text-sm font-semibold mb-1">
            Email<span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="email"
            autoComplete="new-password"
            value={formData.email}
            {...register("email", {
              required: "This field is required",
              validate: (value) => {
                return REGEXES.EMAIL.test(value)
                  ? true
                  : "Please enter a valid email";
              },
            })}
            name="email"
            onChange={handleChange}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.email
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors.email && (
            <p className="text-red-500">{errors?.email?.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="phoneNumber"
            className="block text-sm font-semibold mb-1"
          >
            Phone Number
          </label>
          <PhoneInput
            selectedCode={selectedCode}
            error={errors?.phoneNumber ? true : false}
            selectedNumber={phoneNumber}
            setSelectedNumber={handlePhoneInputChange}
            setSelectedCode={handleCodeChange}
            {...register("phoneNumber", {
              validate: (value) => {
                return (
                  value === undefined ||
                  value?.length === 0 ||
                  value?.length === 10 ||
                  "Phone number must be exactly 10 digits"
                );
              },
            })}
          />
          {errors?.phoneNumber && (
            <p className="text-red-500">{errors?.phoneNumber?.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="companyName"
            className="block text-sm font-semibold mb-1"
          >
            Company Name<span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            autoComplete="new-password"
            value={formData.companyName}
            {...register("companyName", {
              required: "This field is required",
            })}
            onChange={handleChange}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.companyName
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors?.companyName && (
            <p className="text-red-500">{errors?.companyName?.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="companyWebsite"
            className="block text-sm font-semibold mb-1"
          >
            Company Website
          </label>
          <input
            type="text"
            id="companyWebsite"
            autoComplete="new-password"
            value={formData.companyWebsite}
            {...register("companyWebsite", {
              validate: (value) => {
                return (
                  value === undefined ||
                  value == "" ||
                  (REGEXES.URL.test(value)
                    ? true
                    : "Please enter a valid website url")
                );
              },
            })}
            onChange={handleChange}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.companyWebsite
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors?.companyWebsite && (
            <p className="text-red-500">{errors?.companyWebsite?.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label htmlFor="address" className="block text-sm font-semibold mb-1">
            Business Address<span className="text-red-600">*</span>
          </label>
          <div className="grid md:grid-cols-2 grid-cols-1 space-y-4">
            <div className="md:col-span-2 col-span-1">
              <input
                type="text"
                id="businessAddress"
                autoComplete="new-password"
                value={formData.businessAddress}
                {...register("businessAddress", {
                  required: "This field is required",
                })}
                onChange={handleChange}
                placeholder="Type address here"
                className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
                  errors.businessAddress
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-customGreenColor"
                } rounded-lg focus:outline-none focus:ring-1`}
              />
              {errors.businessAddress && (
                <p className="text-red-500">{errors.businessAddress.message}</p>
              )}
            </div>

            <div className="md:col-span-2 col-span-1">
              <input
                type="text"
                id="city"
                autoComplete="new-password"
                value={formData.city}
                {...register("city", { required: "This field is required" })}
                onChange={handleChange}
                name="city"
                placeholder="Type city name here"
                className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
                  errors?.city
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-customGreenColor"
                } rounded-lg focus:outline-none focus:ring-1`}
              />
              {errors?.city && (
                <p className="text-red-500">{errors.city.message}</p>
              )}
            </div>

            <div className="md:col-span-2 col-span-1 gap-x-4 grid md:grid-cols-2 grid-cols-1">
              <div className="col-span-1">
                <StatesDropDown
                  selectedState={selectedState}
                  setSelectedState={handleStateChange}
                  error={errors?.state ? true : false}
                  {...register("state", { required: "This field is required" })}
                />
                {errors?.state && (
                  <p className="text-red-500">{errors?.state?.message}</p>
                )}
              </div>

              <div className="col-span-1 md:mt-1 mt-5 w-full">
                <input
                  type="text"
                  id="zipCode"
                  autoComplete="new-password"
                  value={formData.zipCode}
                  {...register("zipCode", {
                    required: "This field is required",
                  })}
                  onChange={handleChange}
                  name="zipCode"
                  placeholder="Type zip code here"
                  className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
                    errors?.zipCode
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-customGreenColor"
                  } rounded-lg focus:outline-none focus:ring-1`}
                />
                {errors?.zipCode && (
                  <p className="text-red-500">{errors?.zipCode?.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="numberOfStores"
            className="block text-sm font-semibold mb-1"
          >
            Number of Stores<span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="numberOfStores"
            pattern="^\d+$"
            autoComplete="new-password"
            value={formData.numberOfStores}
            {...register("numberOfStores", {
              required: "This field is required",
              validate: (value) => {
                return REGEXES.POSITIVE_INTEGER_INPUT.test(value)
                  ? true
                  : "Please enter a valid value";
              },
            })}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^[1-9]\d*$/.test(value)) {
                handleChange(e);
              }
            }}
            className={`w-full px-3 py-2 xxl:px-4 xxl:py-3 border ${
              errors.numberOfStores
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-customGreenColor"
            } rounded-lg focus:outline-none focus:ring-1`}
          />
          {errors.numberOfStores && (
            <p className="text-red-500">{errors.numberOfStores.message}</p>
          )}
        </div>

        <div className="col-span-1 mb-4">
          <label
            htmlFor="heardAboutUs"
            className="block text-sm font-semibold mb-1"
          >
            How did you hear about us?
          </label>
          <SourceOfInfoDropDown
            infoSource={infoSource}
            setInfoSource={handleSourceOfInfoChange}
            {...register("heardAboutUs")}
          />
        </div>

        <div className="md:col-span-2 col-span-1 mb-4">
          <label htmlFor="message" className="block text-sm font-semibold mb-1">
            Message
          </label>
          <textarea
            id="message"
            value={formData.message}
            {...register("message")}
            onChange={handleChange}
            className="resize-none w-full px-3 py-2 xxl:px-4 xxl:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-customGreenColor"
            rows={4}
            autoComplete="off"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`col-span-1 flex items-center justify-center gap-3 w-max ${
            isLoading ? "cursor-not-allowed" : ""
          } bg-customGreenColor text-white px-5 py-3 rounded-lg mr-1 hover:bg-customGreenDark transition-colors duration-300`}
        >
          {isLoading && (
            <div className="border-white h-4 w-4 animate-spin opacity-50 rounded-full border-2 border-t-customGreenColor" />
          )}
          {isLoading ? "Submitting..." : "Submit"}
        </button>
      </form>
      {isLoading && <Loader />}
    </div>
  );
};

export default ContactUs;
